import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/useAuth'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Clock, Search, Plus, X, RotateCcw, Lock, AlertCircle, Calendar, Shield } from 'lucide-react'

interface Player {
  id: string
  name: string
  position: 'GK' | 'DEF' | 'MID' | 'FWD'
  team: string
  photo_url?: string
  is_active: boolean
}

interface LineupSlot {
  id: string
  position: 'GK' | 'DEF' | 'MID' | 'FWD'
  slotType: 'starter' | 'bench'
  player?: Player
}

interface RosterEditability {
  editable: boolean
  reason?: string
  lockAt: string
  status: string
  lockedAt?: string
}

interface PlayerFixture {
  opponent: string
  date: string
  isHome: boolean
}

const SetLineup = () => {
  const { leagueId, playerId, gwNumber } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuth()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([])
  const [lineup, setLineup] = useState<LineupSlot[]>([])
  const [originalLineup, setOriginalLineup] = useState<LineupSlot[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [positionFilter, setPositionFilter] = useState<string>('all')
  const [teamFilter, setTeamFilter] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lockTime, setLockTime] = useState<Date | null>(null)
  const [timeLeft, setTimeLeft] = useState<string>('')
  const [editability, setEditability] = useState<RosterEditability | null>(null)
  const [showLockModal, setShowLockModal] = useState(false)
  const [playerFixtures, setPlayerFixtures] = useState<Record<string, PlayerFixture>>({})
  const [isLocked, setIsLocked] = useState(false)
  const [gameweekId, setGameweekId] = useState<string | null>(null)
  const [leaguePlayer, setLeaguePlayer] = useState<any>(null)

  // Initialize empty lineup
  const initializeLineup = () => {
    const slots: LineupSlot[] = []
    
    // Starters: 1 GK, 4 DEF, 4 MID, 2 FWD
    slots.push({ id: 'starter-gk-1', position: 'GK', slotType: 'starter' })
    for (let i = 1; i <= 4; i++) {
      slots.push({ id: `starter-def-${i}`, position: 'DEF', slotType: 'starter' })
    }
    for (let i = 1; i <= 4; i++) {
      slots.push({ id: `starter-mid-${i}`, position: 'MID', slotType: 'starter' })
    }
    for (let i = 1; i <= 2; i++) {
      slots.push({ id: `starter-fwd-${i}`, position: 'FWD', slotType: 'starter' })
    }
    
    // Bench: 10 slots (can be any position)
    for (let i = 1; i <= 10; i++) {
      slots.push({ id: `bench-${i}`, position: 'GK', slotType: 'bench' }) // Position flexible for bench
    }
    
    return slots
  }

  // Countdown timer
  const updateCountdown = useCallback(() => {
    if (!lockTime) return
    
    const now = Date.now()
    const lockTimestamp = lockTime.getTime()
    const diff = lockTimestamp - now
    
    if (diff <= 0) {
      setTimeLeft('LOCKED')
      if (editability?.editable) {
        checkEditability() // Re-check if we just passed lock time
      }
      return
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    
    if (days > 0) {
      setTimeLeft(`${days}d ${hours}h ${minutes}m`)
    } else if (hours > 0) {
      setTimeLeft(`${hours}h ${minutes}m ${seconds}s`)
    } else {
      setTimeLeft(`${minutes}m ${seconds}s`)
    }
  }, [lockTime, editability])

  useEffect(() => {
    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [updateCountdown])

  // Get countdown banner color
  const getCountdownColor = () => {
    if (!lockTime) return 'bg-muted'
    
    const diff = lockTime.getTime() - Date.now()
    if (diff <= 0) return 'bg-destructive text-destructive-foreground'
    if (diff < 10 * 60 * 1000) return 'bg-destructive text-destructive-foreground' // < 10 minutes
    if (diff < 60 * 60 * 1000) return 'bg-warning text-warning-foreground' // < 1 hour
    return 'bg-success text-success-foreground'
  }

  // Check editability and fetch data
  useEffect(() => {
    checkEditabilityAndFetchData()
  }, [leagueId, playerId, gwNumber])

  // Auto-refresh editability near lock time
  useEffect(() => {
    if (gameweekId && editability?.editable && lockTime) {
      const timeUntilLock = lockTime.getTime() - Date.now()
      if (timeUntilLock > 0 && timeUntilLock < 5 * 60 * 1000) { // Within 5 minutes
        intervalRef.current = setInterval(checkEditability, 30000) // Check every 30s
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [editability, lockTime, gameweekId])

  const checkEditabilityAndFetchData = async () => {
    if (!leagueId || !playerId || !gwNumber) return

    try {
      setLoading(true)
      await fetchData() // This will set gameweekId
      // checkEditability will be called after gameweekId is set
    } catch (error) {
      console.error('Error loading page data:', error)
      toast({
        title: 'Error',
        description: 'Failed to load page data. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  // Check editability when gameweekId changes
  useEffect(() => {
    if (gameweekId) {
      checkEditability()
    }
  }, [gameweekId])

  const checkEditability = async () => {
    if (!playerId || !gameweekId) return

    try {
      const { data, error } = await supabase.functions.invoke('can-edit-roster', {
        body: {
          leaguePlayerId: playerId,
          gameweekId: gameweekId
        }
      })

      if (error) throw error

      setEditability(data)
      setIsLocked(!data.editable)
      
      if (data.lockAt) {
        setLockTime(new Date(data.lockAt))
      }

      // If not editable anymore, switch to locked state
      if (!data.editable && !isLocked) {
        toast({
          title: 'Roster Locked',
          description: data.reason || 'Your roster has been locked.',
        })
      }
    } catch (error) {
      console.error('Error checking editability:', error)
    }
  }

  const fetchGameweekId = async () => {
    if (!leagueId || !gwNumber) return null

    try {
      const { data, error } = await supabase
        .from('gameweeks')
        .select('id')
        .eq('league_id', leagueId)
        .eq('gw_number', parseInt(gwNumber))
        .maybeSingle()

      if (error) throw error
      return data?.id || null
    } catch (error) {
      console.error('Error fetching gameweek ID:', error)
      return null
    }
  }

  const fetchData = async () => {
    if (!leagueId || !playerId || !gwNumber) return

    try {
      // First get the gameweek UUID
      const gwId = await fetchGameweekId()
      if (!gwId) {
        toast({
          title: 'Error',
          description: 'Gameweek not found.',
          variant: 'destructive',
        })
        return
      }
      
      setGameweekId(gwId)

      // Get league player details first
      const { data: leaguePlayerData, error: playerError } = await supabase
        .from('league_players')
        .select('*')
        .eq('league_id', leagueId)
        .eq('user_id', user?.id)
        .maybeSingle();

      if (playerError) {
        console.error('Error fetching league player:', playerError);
        return;
      }

      if (!leaguePlayerData) {
        toast({
          title: 'Access denied',
          description: 'You are not a member of this league.',
          variant: 'destructive',
        });
        return;
      }

      setLeaguePlayer(leaguePlayerData);

      // Fetch available players and current lineup concurrently
      const [playersResponse, slotsResponse, lineupResponse, fixturesResponse] = await Promise.all([
        supabase.from('players').select('*').eq('is_active', true),
        supabase.from('roster_slots').select(`
          *,
          players (*)
        `).eq('league_player_id', leaguePlayerData.id).eq('gameweek_id', gwId),
        supabase.from('lineup_submissions').select('*').eq('league_player_id', leaguePlayerData.id).eq('gameweek_id', gwId).maybeSingle(),
        supabase.from('matches').select('*').eq('league_id', leagueId)
      ])

      if (playersResponse.error) throw playersResponse.error
      if (slotsResponse.error) throw slotsResponse.error

      setAvailablePlayers(playersResponse.data || [])

      // Process fixtures for player display
      if (fixturesResponse.data) {
        const fixtures: Record<string, PlayerFixture> = {}
        fixturesResponse.data.forEach(match => {
          fixtures[match.home_team] = {
            opponent: match.away_team,
            date: match.scheduled_time,
            isHome: true
          }
          fixtures[match.away_team] = {
            opponent: match.home_team,
            date: match.scheduled_time,
            isHome: false
          }
        })
        setPlayerFixtures(fixtures)
      }

      // Convert to lineup format
      const formattedLineup = initializeLineup()
      
      if (slotsResponse.data && slotsResponse.data.length > 0) {
        slotsResponse.data.forEach(slot => {
          let targetSlot;
          
          if (slot.slot_type === 'starter') {
            targetSlot = formattedLineup.find(
              s => s.position === slot.position && s.slotType === 'starter' && !s.player
            )
          } else {
            targetSlot = formattedLineup.find(
              s => s.slotType === 'bench' && !s.player
            )
          }
          
          if (targetSlot && slot.players) {
            targetSlot.player = slot.players as Player
          }
        })
      }

      setLineup(formattedLineup)
      setOriginalLineup(JSON.parse(JSON.stringify(formattedLineup)))

    } catch (error) {
      console.error('Error fetching data:', error)
      toast({
        title: 'Error',
        description: 'Failed to load lineup data. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const saveLineup = async () => {
    if (!playerId || !gameweekId) return

    // Route guard - check editability before saving
    if (!editability?.editable) {
      toast({
        title: 'Roster is Locked',
        description: 'You cannot edit your roster at this time.',
        variant: 'destructive',
      })
      return
    }

    try {
      setSaving(true)

      // Convert lineup to the format expected by the server
      const slots = lineup
        .filter(slot => slot.player)
        .map(slot => ({
          player_id: slot.player!.id,
          position: slot.position,
          slot_type: slot.slotType
        }))

      const { data, error } = await supabase.functions.invoke('submit-fantasy-lineup', {
        body: {
          leaguePlayerId: playerId,
          gameweekId: gameweekId,
          slots
        }
      })

      if (error) throw error

      toast({
        title: 'Success!',
        description: 'Your lineup has been saved.',
      })

      // Update original lineup and refresh editability
      setOriginalLineup(JSON.parse(JSON.stringify(lineup)))
      await checkEditability()

    } catch (error: any) {
      console.error('Error saving lineup:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to save lineup. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleEarlyLock = async () => {
    if (!playerId || !gameweekId) return

    try {
      setSaving(true)

      const { data, error } = await supabase.functions.invoke('lock-roster', {
        body: {
          leaguePlayerId: playerId,
          gameweekId: gameweekId,
          force: true
        }
      })

      if (error) throw error

      toast({
        title: 'Roster Locked',
        description: 'Your roster has been locked successfully.',
      })

      setShowLockModal(false)
      await checkEditability() // This will update the UI to locked state

    } catch (error: any) {
      console.error('Error locking roster:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to lock roster. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const addPlayer = (player: Player) => {
    if (isLocked) return

    // Find the first available slot for this position
    const availableSlot = lineup.find(
      slot => slot.position === player.position && !slot.player
    )

    if (availableSlot) {
      const newLineup = lineup.map(slot =>
        slot.id === availableSlot.id ? { ...slot, player } : slot
      )
      setLineup(newLineup)
    } else {
      toast({
        title: 'No Available Slots',
        description: `All ${player.position} slots are filled.`,
        variant: 'destructive',
      })
    }
  }

  const removePlayer = (slotId: string) => {
    if (isLocked) return

    const newLineup = lineup.map(slot =>
      slot.id === slotId ? { ...slot, player: undefined } : slot
    )
    setLineup(newLineup)
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || isLocked) return

    const { source, destination } = result
    
    // Handle drag between different containers
    if (source.droppableId !== destination.droppableId) {
      const sourceSlot = lineup.find(slot => slot.id === source.droppableId)
      const destSlot = lineup.find(slot => slot.id === destination.droppableId)
      
      if (sourceSlot && destSlot) {
        // Swap players between slots
        const newLineup = lineup.map(slot => {
          if (slot.id === source.droppableId) {
            return { ...slot, player: destSlot.player }
          }
          if (slot.id === destination.droppableId) {
            return { ...slot, player: sourceSlot.player }
          }
          return slot
        })
        setLineup(newLineup)
      }
    }
  }

  const resetLineup = () => {
    if (isLocked) return
    setLineup(JSON.parse(JSON.stringify(originalLineup)))
    toast({
      title: 'Changes Reset',
      description: 'Lineup has been reset to last saved version.',
    })
  }

  const hasChanges = () => {
    return JSON.stringify(lineup) !== JSON.stringify(originalLineup)
  }

  const getLineupValidation = () => {
    const starters = lineup.filter(slot => slot.slotType === 'starter')
    const goalkeepers = starters.filter(slot => slot.position === 'GK' && slot.player).length
    const totalPlayers = lineup.filter(slot => slot.player).length
    
    if (goalkeepers === 0) return 'Missing goalkeeper'
    if (goalkeepers > 1) return 'Too many goalkeepers'
    if (totalPlayers < 11) return `Need ${11 - totalPlayers} more players`
    
    return `${totalPlayers}/21 players selected`
  }

  // Filter available players
  const filteredPlayers = availablePlayers.filter(player => {
    const matchesSearch = player.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesPosition = positionFilter === 'all' || player.position === positionFilter
    const matchesTeam = teamFilter === 'all' || player.team === teamFilter
    const notInLineup = !lineup.some(slot => slot.player?.id === player.id)
    
    return matchesSearch && matchesPosition && matchesTeam && notInLineup
  })

  const teams = Array.from(new Set(availablePlayers.map(p => p.team))).sort()

  const PlayerCard = ({ player, onRemove, showRemove = true, fixture }: { 
    player: Player
    onRemove?: () => void
    showRemove?: boolean
    fixture?: PlayerFixture
  }) => (
    <div className="flex items-center gap-3 p-2 bg-card border rounded-lg">
      <Avatar className="h-10 w-10">
        <AvatarImage src={player.photo_url} />
        <AvatarFallback className="text-xs">
          {player.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{player.name}</p>
        <p className="text-xs text-muted-foreground">{player.team}</p>
        <div className="flex items-center gap-1 mt-1">
          <Badge variant="outline" className="text-xs">
            {player.position}
          </Badge>
          {fixture && (
            <Badge variant="secondary" className="text-xs">
              {fixture.isHome ? 'vs' : '@'} {fixture.opponent}
            </Badge>
          )}
        </div>
        {fixture && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <Calendar className="h-3 w-3" />
            {new Date(fixture.date).toLocaleDateString()}
          </p>
        )}
      </div>
      {showRemove && onRemove && !isLocked && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  )

  // Locked state banner
  const LockedBanner = () => (
    <div className="border-b bg-muted/30">
      <div className="container mx-auto px-4 py-4">
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <span className="font-semibold">Roster locked for Gameweek {gwNumber}</span>
              {editability?.lockedAt && (
                <span className="ml-2 text-sm text-muted-foreground">
                  Locked at {new Date(editability.lockedAt).toLocaleString()}
                </span>
              )}
            </div>
            <Badge variant="secondary" className="text-xs">
              Edits open again next gameweek
            </Badge>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading lineup...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Set Your Lineup — Gameweek {gwNumber}</h1>
              <p className="text-muted-foreground">
                {isLocked ? 'View your locked lineup' : 'Drag players between positions or use the add/remove buttons'}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/league/${leagueId}`)}
              className="gap-2"
            >
              ← Back to League
            </Button>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {isLocked ? (
        <LockedBanner />
      ) : lockTime && (
        <div className={`border-b ${getCountdownColor()}`}>
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4" />
              <span>
                Locks in {timeLeft} (at {lockTime.toLocaleString()})
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-12 gap-6">
            {/* Left Panel - Player Picker */}
            <div className="lg:col-span-3">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Player Picker
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isLocked ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Lock className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm">Player selection is locked</p>
                    </div>
                  ) : (
                    <>
                      {/* Search Input */}
                      <Input
                        placeholder="Search players..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full"
                      />
                      
                      {/* Filters */}
                      <div className="space-y-2">
                        <Select value={positionFilter} onValueChange={setPositionFilter}>
                          <SelectTrigger>
                            <SelectValue placeholder="Position" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Positions</SelectItem>
                            <SelectItem value="GK">Goalkeeper</SelectItem>
                            <SelectItem value="DEF">Defender</SelectItem>
                            <SelectItem value="MID">Midfielder</SelectItem>
                            <SelectItem value="FWD">Forward</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select value={teamFilter} onValueChange={setTeamFilter}>
                          <SelectTrigger>
                            <SelectValue placeholder="Team" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Teams</SelectItem>
                            {teams.map(team => (
                              <SelectItem key={team} value={team}>{team}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Available Players */}
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {filteredPlayers.map(player => (
                          <div key={player.id} className="flex items-center justify-between p-2 rounded-lg border bg-card hover:bg-accent transition-colors">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={player.photo_url} />
                                <AvatarFallback className="text-xs">
                                  {player.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-semibold text-sm">{player.name}</p>
                                <p className="text-xs text-muted-foreground">{player.team} • {player.position}</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => addPlayer(player)}
                              className="gap-1"
                            >
                              <Plus className="h-3 w-3" />
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Main Lineup Grid */}
            <div className="lg:col-span-9">
              <div className="space-y-6">
                {/* Starters */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Starting XI
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4">
                      {/* Formation Layout */}
                      <div className="space-y-4">
                        {/* Goalkeeper */}
                        <div className="text-center">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Goalkeeper</h4>
                          <div className="flex justify-center">
                            {lineup.filter(slot => slot.slotType === 'starter' && slot.position === 'GK').map(slot => (
                              <Droppable key={slot.id} droppableId={slot.id}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`w-48 min-h-[60px] p-2 border-2 border-dashed rounded-lg transition-colors ${
                                      snapshot.isDraggingOver ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'
                                    }`}
                                  >
                                    {slot.player ? (
                                      isLocked ? (
                                        <PlayerCard
                                          player={slot.player}
                                          onRemove={() => removePlayer(slot.id)}
                                          showRemove={false}
                                          fixture={playerFixtures[slot.player.team]}
                                        />
                                      ) : (
                                        <Draggable draggableId={slot.id} index={0}>
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className={`${snapshot.isDragging ? 'opacity-75' : ''}`}
                                            >
                                              <PlayerCard
                                                player={slot.player}
                                                onRemove={() => removePlayer(slot.id)}
                                                fixture={playerFixtures[slot.player.team]}
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    ) : (
                                      <div className="h-16 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center text-xs text-muted-foreground">
                                        Empty
                                      </div>
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            ))}
                          </div>
                        </div>

                        {/* Defenders */}
                        <div className="text-center">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Defenders</h4>
                          <div className="grid grid-cols-4 gap-2 max-w-4xl mx-auto">
                            {lineup.filter(slot => slot.slotType === 'starter' && slot.position === 'DEF').map(slot => (
                              <Droppable key={slot.id} droppableId={slot.id}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`min-h-[60px] p-2 border-2 border-dashed rounded-lg transition-colors ${
                                      snapshot.isDraggingOver ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'
                                    }`}
                                  >
                                    {slot.player ? (
                                      isLocked ? (
                                        <PlayerCard
                                          player={slot.player}
                                          onRemove={() => removePlayer(slot.id)}
                                          showRemove={false}
                                          fixture={playerFixtures[slot.player.team]}
                                        />
                                      ) : (
                                        <Draggable draggableId={slot.id} index={0}>
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className={`${snapshot.isDragging ? 'opacity-75' : ''}`}
                                            >
                                              <PlayerCard
                                                player={slot.player}
                                                onRemove={() => removePlayer(slot.id)}
                                                fixture={playerFixtures[slot.player.team]}
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    ) : (
                                      <div className="h-16 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center text-xs text-muted-foreground">
                                        Empty
                                      </div>
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            ))}
                          </div>
                        </div>

                        {/* Midfielders */}
                        <div className="text-center">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Midfielders</h4>
                          <div className="grid grid-cols-4 gap-2 max-w-4xl mx-auto">
                            {lineup.filter(slot => slot.slotType === 'starter' && slot.position === 'MID').map(slot => (
                              <Droppable key={slot.id} droppableId={slot.id}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`min-h-[60px] p-2 border-2 border-dashed rounded-lg transition-colors ${
                                      snapshot.isDraggingOver ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'
                                    }`}
                                  >
                                    {slot.player ? (
                                      isLocked ? (
                                        <PlayerCard
                                          player={slot.player}
                                          onRemove={() => removePlayer(slot.id)}
                                          showRemove={false}
                                          fixture={playerFixtures[slot.player.team]}
                                        />
                                      ) : (
                                        <Draggable draggableId={slot.id} index={0}>
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className={`${snapshot.isDragging ? 'opacity-75' : ''}`}
                                            >
                                              <PlayerCard
                                                player={slot.player}
                                                onRemove={() => removePlayer(slot.id)}
                                                fixture={playerFixtures[slot.player.team]}
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    ) : (
                                      <div className="h-16 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center text-xs text-muted-foreground">
                                        Empty
                                      </div>
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            ))}
                          </div>
                        </div>

                        {/* Forwards */}
                        <div className="text-center">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Forwards</h4>
                          <div className="grid grid-cols-2 gap-2 max-w-lg mx-auto">
                            {lineup.filter(slot => slot.slotType === 'starter' && slot.position === 'FWD').map(slot => (
                              <Droppable key={slot.id} droppableId={slot.id}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`min-h-[60px] p-2 border-2 border-dashed rounded-lg transition-colors ${
                                      snapshot.isDraggingOver ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'
                                    }`}
                                  >
                                    {slot.player ? (
                                      isLocked ? (
                                        <PlayerCard
                                          player={slot.player}
                                          onRemove={() => removePlayer(slot.id)}
                                          showRemove={false}
                                          fixture={playerFixtures[slot.player.team]}
                                        />
                                      ) : (
                                        <Draggable draggableId={slot.id} index={0}>
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className={`${snapshot.isDragging ? 'opacity-75' : ''}`}
                                            >
                                              <PlayerCard
                                                player={slot.player}
                                                onRemove={() => removePlayer(slot.id)}
                                                fixture={playerFixtures[slot.player.team]}
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    ) : (
                                      <div className="h-16 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center text-xs text-muted-foreground">
                                        Empty
                                      </div>
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Bench */}
                <Card>
                  <CardHeader>
                    <CardTitle>Bench (10 slots)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-2">
                      {lineup.filter(slot => slot.slotType === 'bench').map(slot => (
                        <Droppable key={slot.id} droppableId={slot.id}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`min-h-[60px] p-2 border-2 border-dashed rounded-lg transition-colors ${
                                snapshot.isDraggingOver ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'
                              }`}
                            >
                              {slot.player ? (
                                isLocked ? (
                                  <PlayerCard
                                    player={slot.player}
                                    onRemove={() => removePlayer(slot.id)}
                                    showRemove={false}
                                    fixture={playerFixtures[slot.player.team]}
                                  />
                                ) : (
                                  <Draggable draggableId={slot.id} index={0}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`${snapshot.isDragging ? 'opacity-75' : ''}`}
                                      >
                                        <PlayerCard
                                          player={slot.player}
                                          onRemove={() => removePlayer(slot.id)}
                                          fixture={playerFixtures[slot.player.team]}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              ) : (
                                <div className="h-16 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center text-xs text-muted-foreground">
                                  Empty
                                </div>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </DragDropContext>
      </div>

      {/* Sticky Bottom Actions */}
      <div className="sticky bottom-0 bg-background border-t">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {isLocked ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Lock className="h-4 w-4" />
                  Roster is locked
                </div>
              ) : (
                <>
                  <Button
                    onClick={saveLineup}
                    disabled={saving || !hasChanges()}
                    className="gap-2"
                  >
                    {saving ? 'Saving...' : 'Save Lineup'}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={resetLineup}
                    disabled={saving || !hasChanges()}
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset Changes
                  </Button>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                {getLineupValidation()}
              </div>
              
              {editability?.editable && !isLocked && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowLockModal(true)}
                  className="gap-2"
                >
                  <Lock className="h-4 w-4" />
                  Lock Now
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Early Lock Modal */}
      <Dialog open={showLockModal} onOpenChange={setShowLockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Lock Roster Now?
            </DialogTitle>
            <DialogDescription>
              Lock your roster now? You cannot edit after locking.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLockModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEarlyLock}
              disabled={saving}
              className="gap-2"
            >
              {saving ? 'Locking...' : 'Confirm Lock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default SetLineup
